// Uppgifterna som projektnivån räknas fram ur. Nivån går inte att välja –
// den följer av dessa fakta och räknas om av servern när något ändras.
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  EMPTY_SCALE_FACTS,
  fetchScaleFacts,
  saveScaleFacts,
  type ScaleFacts,
} from "@/lib/basProcess/projectLevel";

interface Props {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: () => void;
}

const num = (value: string): number | null => {
  const trimmed = value.trim().replace(/\s/g, "").replace(",", ".");
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const str = (value: number | null) => (value === null ? "" : String(value));

const ScaleFactsDialog: React.FC<Props> = ({ projectId, open, onOpenChange, onSaved }) => {
  const [facts, setFacts] = useState<ScaleFacts>(EMPTY_SCALE_FACTS);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    void fetchScaleFacts(projectId).then(setFacts);
  }, [open, projectId]);

  const set = <K extends keyof ScaleFacts>(key: K, value: ScaleFacts[K]) =>
    setFacts((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    setBusy(true);
    try {
      await saveScaleFacts(projectId, facts);
      toast.success("Uppgifterna sparade – nivån har räknats om");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Kunde inte spara uppgifterna");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Uppgifter om projektets omfattning</DialogTitle>
          <DialogDescription>
            Nivån i projektet räknas fram ur de här uppgifterna. Den kan inte väljas manuellt, och
            den höjs automatiskt om projektet visar sig vara större än vad som angetts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="contract_value">Kontraktssumma (kr)</Label>
              <Input
                id="contract_value"
                inputMode="numeric"
                value={str(facts.contract_value)}
                onChange={(e) => set("contract_value", num(e.target.value))}
                placeholder="t.ex. 4 500 000"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="gross_area">Bruttoarea (m²)</Label>
              <Input
                id="gross_area"
                inputMode="numeric"
                value={str(facts.gross_area)}
                onChange={(e) => set("gross_area", num(e.target.value))}
                placeholder="t.ex. 850"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="duration_months">Byggtid (månader)</Label>
              <Input
                id="duration_months"
                inputMode="numeric"
                value={str(facts.duration_months)}
                onChange={(e) => set("duration_months", num(e.target.value))}
                placeholder="t.ex. 6"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contractor_count">Antal entreprenörer</Label>
              <Input
                id="contractor_count"
                inputMode="numeric"
                value={str(facts.contractor_count)}
                onChange={(e) => set("contractor_count", num(e.target.value))}
                placeholder="t.ex. 3"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="special_risk_count">Antal särskilda risker (AFS 2023:3)</Label>
            <Input
              id="special_risk_count"
              inputMode="numeric"
              value={str(facts.special_risk_count)}
              onChange={(e) => set("special_risk_count", num(e.target.value))}
              placeholder="0–13"
            />
            <p className="text-xs text-muted-foreground">
              Systemet räknar även själv utifrån riskbedömningarna och använder det högsta värdet.
            </p>
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Förhandsanmälan krävs</p>
              <p className="text-xs text-muted-foreground">
                Över 30 arbetsdagar med mer än 20 personer, eller mer än 500 persondagar.
              </p>
            </div>
            <Switch
              checked={facts.prior_notification_required}
              onCheckedChange={(v) => set("prior_notification_required", v)}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="street_address">Gatuadress</Label>
              <Input
                id="street_address"
                value={facts.street_address ?? ""}
                onChange={(e) => set("street_address", e.target.value || null)}
                placeholder="t.ex. Storgatan 12"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="property_designation">Fastighetsbeteckning</Label>
              <Input
                id="property_designation"
                value={facts.property_designation ?? ""}
                onChange={(e) => set("property_designation", e.target.value || null)}
                placeholder="t.ex. Kv Björnen 3"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Adress och fastighetsbeteckning används för att upptäcka om ett och samma bygge delats
            upp på flera projekt.
          </p>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Avbryt
          </Button>
          <Button onClick={save} disabled={busy}>
            Spara och räkna om
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScaleFactsDialog;
